
import { computed, defineComponent, onMounted } from "vue";
import { useRoute } from "vue-router";

import {
  ColumnDefinition,
  EditIcon,
  BaseButton,
  DeleteIcon,
  BaseTable,
  SouthIcon,
  NorthIcon,
} from "@tager/admin-ui";
import {
  Nullable,
  useResource,
  useResourceDelete,
  useResourceMove,
  useToast,
} from "@tager/admin-services";
import { Page } from "@tager/admin-layout";

import {
  deleteField,
  getFieldList,
  getProductType,
  moveField,
} from "@/services/requests";
import {
  FieldType,
  FullProductType,
  FullProductTypeType,
} from "@/typings/model";
import {
  getFieldListUrl,
  getProductListUrl,
  getFieldCreateFormUrl,
  getFieldEditFormUrl,
} from "@/utils/paths";

const COLUMN_DEFS: Array<ColumnDefinition<FieldType>> = [
  {
    id: 1,
    name: "Название",
    field: "name",
  },
  {
    id: 2,
    name: "Системное название",
    field: "systemName",
  },
  {
    id: 3,
    name: "Тип",
    field: "type",
  },
  {
    id: 4,
    name: "Действия",
    field: "actions",
    style: { width: "101px", textAlign: "center", whiteSpace: "nowrap" },
    headStyle: { width: "101px", textAlign: "center" },
  },
];

export default defineComponent({
  name: "FieldList",
  components: {
    Page,
    EditIcon,
    BaseButton,
    DeleteIcon,
    BaseTable,
    SouthIcon,
    NorthIcon,
  },
  setup() {
    const route = useRoute();

    const productTypeId = computed(() => route.params.productTypeId as string);

    const [
      fetchProductType,
      { data: productType, loading: isProductTypeLoading },
    ] = useResource<Nullable<FullProductTypeType>>({
      fetchResource: () => getProductType(productTypeId.value),
      initialValue: null,
      resourceName: "productType",
    });

    const [
      fetchFieldList,
      { data: fieldList, loading: isFieldListLoading, error },
    ] = useResource<Array<FieldType>>({
      fetchResource: () => getFieldList(productTypeId.value),
      initialValue: [],
      resourceName: "Field list",
    });

    onMounted(() => {
      fetchProductType();
      fetchFieldList();
    });

    const { handleResourceDelete: handleFieldDelete, isDeleting } =
      useResourceDelete({
        deleteResource: deleteField,
        resourceName: "Field",
        onSuccess: fetchFieldList,
      });

    const { handleResourceMove: handleFieldMove, isMoving } = useResourceMove({
      moveResource: moveField,
      resourceName: "Field",
      onSuccess: fetchFieldList,
    });

    const isRowDataLoading = computed<boolean>(() => isFieldListLoading.value);

    function isBusy(fieldId: number): boolean {
      return (
        isDeleting(fieldId) ||
        isMoving(fieldId) ||
        isRowDataLoading.value ||
        isProductTypeLoading.value
      );
    }

    const pageTitle = computed<string>(() => {
      return productType.value
        ? `Поля типа товаров "${productType.value.name}"`
        : `Поля типа товаров ""`;
    });

    function typeTranslation(type: string) {
      if (type === "SELECT") {
        return "Выбор из вариантов";
      }
      if (type === "NUMBER") {
        return "Число";
      }
      if (type === "TRUE_FALSE") {
        return "Да / Нет";
      }
      if (type === "COLOR") {
        return "Цвет";
      }
      if (type === "STRING") {
        return "Текстовое поле";
      }
    }

    return {
      columnDefs: COLUMN_DEFS,
      rowData: fieldList,
      isRowDataLoading,
      errorMessage: error,
      isBusy,
      handleFieldDelete,
      handleFieldMove,
      getFieldCreateFormUrl,
      getFieldEditFormUrl,
      getFieldListUrl,
      getProductListUrl,
      productTypeId,
      pageTitle,
      typeTranslation,
    };
  },
});
